import { GetServerSideProps } from 'next'
import strapi from 'services/strapi'
import BlogPost, {
  BlogPostBook,
  BlogPostComponent,
  BlogPostText,
} from 'models/blog-post'
import Layout from 'components/layout'
import Avatar from 'components/avatar'
import Markdown from 'components/markdown'
import api from 'services/api'
import bluebird from 'bluebird'
import BookDetails from 'models/book-details'
import Cover from 'components/cover'
import Link from 'next/link'
import routes from 'routes'
import DateView from 'components/date-view'
import { NextSeo } from 'next-seo'
import { HOST } from 'config'
import Breadcrumb from 'components/breadcrumb'
import { Heading } from 'pages'
import TagList from 'components/tag-list'
import AppDownload from 'components/app-download'
import Tag from 'models/tag'
import JsonLd, { CommonJsonLd } from 'components/json-ld'
import { copyToClipboard } from 'utils/copy'
import Toast from 'components/Toast'

export const BlogPostPage = ({
  post,
  books,
  posts,
  path,
  tags,
}: {
  post: BlogPost
  books: BookDetails[]
  posts: BlogPost[]
  path: string
  tags: Tag[]
}): JSX.Element => {
  const titleImageUrl =
    post.title_image?.formats?.large?.url ??
    post.title_image?.formats?.medium?.url ??
    post.title_image?.formats?.small?.url

  return (
    <Layout showDownloadBtn={true} downloadBtnText="Read the book on APP">
      <NextSeo
        title={`${post.title} - AnyStories`}
        description={post.meta_description}
        canonical={`${HOST}${path}`}
        openGraph={{
          title: post.title,
          url: `${HOST}${path}`,
          type: 'article',
          images: [
            {
              url: post.title_image?.url,
            },
          ],
        }}
      />
      <CommonJsonLd />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'AnyStories',
              item: HOST,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: `${HOST}/blog`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: post.title,
              item: HOST + routes.blog.post({ slug: post.slug }),
            },
          ],
        }}
      />
      <section className="desktop:flex">
        <section className="desktop:w-[1018px] mb-4">
          <div className="hidden lg:block px-4 py-3">
            <Breadcrumb
              className="mr-4"
              items={[
                {
                  name: 'Blog',
                  link: '/blog',
                },
                {
                  name: post.title,
                },
              ]}
            />
          </div>
          <article className="p-4 pb-16 font-body">
            <h1 className="text-3xl font-bold mb-2">{post.title}</h1>
            <div className="py-2 mb-2 text-sm font-sans flex items-center">
              <Avatar
                src={post.editor.avatar.formats.thumbnail.url}
                name={post.editor.name}
                className="w-5 h-5"
              />
              <span className="pl-1 font-medium">{post.editor.name}</span>
              <span className="text-gray-400 px-2"> | </span>
              <span className="text-gray-400">
                Updated on <DateView date={post.updatedAt} />
              </span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center gap-x-2">
                <span className="text-gray-400">Share:</span>
                <a
                  href={`https://www.facebook.com/sharer.php?u=${HOST}${path}&t=${post.title}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-fb.svg"
                    className="w-6 h-6"
                    alt="facebook"
                  />
                </a>
                <a
                  href={`https://twitter.com/share/?url=${HOST}${path}&text=${post.title}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-twitter.svg"
                    className="w-6 h-6"
                    alt="twitter"
                  />
                </a>
                <a
                  href={`https://pinterest.com/pin/create/button/?url=${HOST}${path}&media=${titleImageUrl}&description=${post.title}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-pinterest.svg"
                    className="w-6 h-6"
                    alt="pinterest"
                  />
                </a>
                <a
                  href={`https://api.whatsapp.com/send?text=${post.title}, ${HOST}${path}&via=lopscoop`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-whatsapp.svg"
                    className="w-6 h-6"
                    alt="whatsapp"
                  />
                </a>
                <a
                  href={`https://www.reddit.com/submit?url=${HOST}${path}&title=${post.title}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-reddit.svg"
                    className="w-6 h-6"
                    alt="reddit"
                  />
                </a>
                <span
                  className="flex items-center justify-center bg-gray-300 rounded-full w-6 h-6 cursor-pointer"
                  onClick={() => {
                    copyToClipboard(`${HOST}${path}`)
                    Toast.show({
                      content: 'The link has been pasted to your clipboard',
                      delay: 1000,
                    })
                  }}
                >
                  <svg
                    className="w-3 h-3 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                    />
                  </svg>
                </span>
              </div>
            </div>
            {titleImageUrl && (
              <img
                src={titleImageUrl}
                className="my-4 rounded-lg w-full"
                alt={post.title}
              />
            )}
            {post.introduction && (
              <Markdown
                markdown={post.introduction}
                className="mb-8 prose-lg"
              />
            )}
            <>
              {post.content.map((block) => (
                <BodyBlock block={block} key={block.id} books={books} />
              ))}
            </>
          </article>
        </section>
        <section className="desktop:w-[422px]">
          <section className="px-4 mb-[40px]">
            <Heading title="Latest Articles" moreLink={routes.blog.list()} />
            <PostList posts={posts} />
          </section>
          <section className="px-4 mb-[40px]">
            <Heading title="Tags" moreLink={routes.tag.list()} />
            <TagList tags={tags} />
          </section>
          <section className="px-4 mb-[40px]">
            <AppDownload />
          </section>
        </section>
      </section>
    </Layout>
  )
}

const BodyBlock = ({
  block,
  books,
}: {
  block: BlogPostComponent
  books: BookDetails[]
}): JSX.Element => {
  let blockView: JSX.Element

  switch (block.__component) {
    case 'blog-post.book':
      blockView = BodyBook({ block: block as unknown as BlogPostBook, books })
      break
    case 'blog-post.text':
      blockView = BodyText({ block: block as unknown as BlogPostText })
      break
  }

  return (
    <>
      {/* <hr className="border-gray-300 my-8" /> */}
      <section className="my-8 clear-both">{blockView}</section>
    </>
  )
}

const BodyBook = ({
  block,
  books,
}: {
  block: BlogPostBook
  books: BookDetails[]
}): JSX.Element => {
  const book = books
    .filter((book) => !!book)
    .find((book) => book.book.id === block.book_id)

  if (!book) return <></>

  const url = routes.story.details(book.book)

  return (
    <>
      <h3 className="text-2xl font-bold underline">
        <Link href={url}>{block.book_title}</Link>
      </h3>
      <div className="text-lg italic text-gray-500">By {book.author.name}</div>
      <div className="pb-4 pt-3">
        <Cover
          book={book.book}
          className="float-right w-24 shadow-lg ml-4 mb-4"
        />
        <Markdown
          markdown={
            block.description?.trim().length > 0
              ? block.description
              : book.book.description
          }
          className="prose-lg"
        />
      </div>

      <div className="font-bold mb-16">
        <Link href={url} className="text-lg text-goldenrod underline">
          Read Now
        </Link>
      </div>
    </>
  )
}

const BodyText = ({ block }: { block: BlogPostText }): JSX.Element => {
  return <Markdown markdown={block.text} className="prose-lg" />
}

const PostList = ({ posts }: { posts: BlogPost[] }): JSX.Element => {
  return (
    <div>
      <ul className="">
        {posts.map((post, index) => (
          <li
            key={post.slug + index}
            className={`block mb-2 rounded bg-gray-100 ${
              index > 15 ? 'hidden' : ''
            }`}
          >
            <Link
              href={routes.blog.post({ slug: post.slug })}
              className="block px-4 py-3"
            >
              <h3 className="font-bold font-body mb-1">{post.title}</h3>
              <div className="text-xs text-gray-400">
                Updated on <DateView date={post.updatedAt} />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
// export async function getStaticPaths() {
//   return {
//     paths: [{ params: { slug: 'most-popular-erotic-romance-novels' } }],
//     fallback: false,
//   }
// }

// export const getStaticProps: GetStaticProps = async ({ params }) => {
//   const slug = params.slug as string
//   const post = await strapi.blogPost.getOne(slug)

//   if (!post) return { notFound: true }

//   const bookIds = post.content
//     .filter((c) => c.__component === 'blog-post.book')
//     .map((c) => ((c as unknown) as BlogPostBook).book_id)

//   const books = await bluebird.map(
//     bookIds,
//     (id: string) => api.book.getBookInfo(id),
//     { concurrency: 50 }
//   )

//   return {
//     props: {
//       post,
//       books,
//       path: routes.blog.post({ slug }),
//     },
//   }
// }

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const slug = params.slug as string
  const post = await strapi.blogPost.getOne(slug)

  if (!post) return { notFound: true }

  const bookIds = post.content
    .filter((c) => c.__component === 'blog-post.book')
    .map((c) => (c as unknown as BlogPostBook).book_id)

  const books = await bluebird.map(
    bookIds,
    (id: string) => api.book.getBookInfo(id),
    { concurrency: 50 }
  )

  const tags = await api.tag.getHomeTags()

  const posts = await strapi.blogPost.getList({
    start: 1,
    limit: 10,
  })

  return {
    props: {
      post,
      posts,
      books,
      path: routes.blog.post({ slug }),
      tags,
    },
  }
}

export default BlogPostPage
